import { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ChevronDown, Check } from 'lucide-react';

// Top 3 pinned first (UK, France, Israel), then the rest alphabetically (FR names).
const COUNTRIES = [
  { code: 'GB', dial: '+44', name: 'Royaume-Uni', flag: '🇬🇧' },
  { code: 'FR', dial: '+33', name: 'France', flag: '🇫🇷' },
  { code: 'IL', dial: '+972', name: 'Israël', flag: '🇮🇱' },
  { code: 'DE', dial: '+49', name: 'Allemagne', flag: '🇩🇪' },
  { code: 'AD', dial: '+376', name: 'Andorre', flag: '🇦🇩' },
  { code: 'SA', dial: '+966', name: 'Arabie Saoudite', flag: '🇸🇦' },
  { code: 'AU', dial: '+61', name: 'Australie', flag: '🇦🇺' },
  { code: 'AT', dial: '+43', name: 'Autriche', flag: '🇦🇹' },
  { code: 'BE', dial: '+32', name: 'Belgique', flag: '🇧🇪' },
  { code: 'BR', dial: '+55', name: 'Brésil', flag: '🇧🇷' },
  { code: 'CA', dial: '+1', name: 'Canada', flag: '🇨🇦' },
  { code: 'CN', dial: '+86', name: 'Chine', flag: '🇨🇳' },
  { code: 'KR', dial: '+82', name: 'Corée du Sud', flag: '🇰🇷' },
  { code: 'CI', dial: '+225', name: "Côte d'Ivoire", flag: '🇨🇮' },
  { code: 'DK', dial: '+45', name: 'Danemark', flag: '🇩🇰' },
  { code: 'AE', dial: '+971', name: 'Émirats Arabes Unis', flag: '🇦🇪' },
  { code: 'ES', dial: '+34', name: 'Espagne', flag: '🇪🇸' },
  { code: 'US', dial: '+1', name: 'États-Unis', flag: '🇺🇸' },
  { code: 'FI', dial: '+358', name: 'Finlande', flag: '🇫🇮' },
  { code: 'GR', dial: '+30', name: 'Grèce', flag: '🇬🇷' },
  { code: 'IN', dial: '+91', name: 'Inde', flag: '🇮🇳' },
  { code: 'IE', dial: '+353', name: 'Irlande', flag: '🇮🇪' },
  { code: 'IT', dial: '+39', name: 'Italie', flag: '🇮🇹' },
  { code: 'JP', dial: '+81', name: 'Japon', flag: '🇯🇵' },
  { code: 'LB', dial: '+961', name: 'Liban', flag: '🇱🇧' },
  { code: 'LU', dial: '+352', name: 'Luxembourg', flag: '🇱🇺' },
  { code: 'MA', dial: '+212', name: 'Maroc', flag: '🇲🇦' },
  { code: 'MC', dial: '+377', name: 'Monaco', flag: '🇲🇨' },
  { code: 'NO', dial: '+47', name: 'Norvège', flag: '🇳🇴' },
  { code: 'NL', dial: '+31', name: 'Pays-Bas', flag: '🇳🇱' },
  { code: 'PT', dial: '+351', name: 'Portugal', flag: '🇵🇹' },
  { code: 'CZ', dial: '+420', name: 'République Tchèque', flag: '🇨🇿' },
  { code: 'RU', dial: '+7', name: 'Russie', flag: '🇷🇺' },
  { code: 'SG', dial: '+65', name: 'Singapour', flag: '🇸🇬' },
  { code: 'SE', dial: '+46', name: 'Suède', flag: '🇸🇪' },
  { code: 'CH', dial: '+41', name: 'Suisse', flag: '🇨🇭' },
  { code: 'TN', dial: '+216', name: 'Tunisie', flag: '🇹🇳' },
  { code: 'TR', dial: '+90', name: 'Turquie', flag: '🇹🇷' },
];

function parseValue(value) {
  if (!value) return { dial: '+44', number: '' };
  const match = COUNTRIES.find((c) => value.startsWith(c.dial + ' ') || value.startsWith(c.dial));
  if (match) return { dial: match.dial, number: value.slice(match.dial.length).trim() };
  return { dial: '+44', number: value };
}

export default function PhoneInput({ label, value, onChange, testid }) {
  const { dial, number } = useMemo(() => parseValue(value), [value]);
  const [open, setOpen] = useState(false);
  const selected = COUNTRIES.find((c) => c.dial === dial) || COUNTRIES[0];

  const emit = (newDial, newNumber) => onChange(`${newDial} ${newNumber}`.trim());

  return (
    <div className="space-y-1">
      {label && <Label className="text-xs text-rolex-green/70">{label}</Label>}
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button"
              className="flex items-center gap-2 rounded-xl h-11 px-3 border border-input bg-white text-sm shrink-0 hover:border-primary/60 transition-colors"
              data-testid={testid ? `${testid}-prefix` : 'phone-prefix'}>
              <img src={`https://flagcdn.com/24x18/${selected.code.toLowerCase()}.png`} alt={selected.name} className="w-6 h-[18px] rounded-[2px] object-cover shrink-0" loading="lazy" />
              <span className="font-mono font-semibold text-rolex-green">{selected.dial}</span>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-72" align="start">
            <Command>
              <CommandInput placeholder="Rechercher un pays…" className="text-sm" />
              <CommandList>
                <CommandEmpty>Aucun pays</CommandEmpty>
                <CommandGroup>
                  {COUNTRIES.map((c) => (
                    <CommandItem key={c.code} value={`${c.name} ${c.dial}`}
                      onSelect={() => { emit(c.dial, number); setOpen(false); }}
                      className="gap-2.5 py-2.5 cursor-pointer aria-selected:bg-gold/15"
                      data-testid={`phone-country-${c.code}`}>
                      <img src={`https://flagcdn.com/24x18/${c.code.toLowerCase()}.png`} alt={c.name} className="w-6 h-[18px] rounded-[2px] object-cover shrink-0" loading="lazy" />
                      <span className="flex-1 text-sm font-medium text-rolex-green">{c.name}</span>
                      <span className="font-mono text-xs font-semibold text-rolex-green/70">{c.dial}</span>
                      {c.dial === dial && <Check className="w-4 h-4 text-primary shrink-0" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input type="tel" inputMode="tel" value={number}
          onChange={(e) => emit(dial, e.target.value.replace(/[^\d\s]/g, ''))}
          placeholder="7911 123456" className="rounded-xl h-11 flex-1"
          data-testid={testid} />
      </div>
    </div>
  );
}
